import React, { useEffect, useMemo, useState } from 'react';
import { postFetch } from '../../../../fetch/fetch';
import totalLocationData from '../../../../data/locationdata';
import {
    BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// ─── 라이트 모드 테마 (InflowPage와 동일 값 — 변경 금지) ─────────────────────
const PAGE_BG   = "#F3F4F6";
const CARD_BG   = "#FFFFFF";
const BORDER    = "#E5E7EB";
const TEXT_MAIN = "#111827";
const TEXT_SUB  = "#6B7280";

// 등록률 색상 임계값
const GREEN  = "#10B981";   // ≥ 60%
const YELLOW = "#F59E0B";   // 40 ~ 59%
const RED    = "#EF4444";   // < 40%

const colorByRate = (r: number): string => r >= 60 ? GREEN : r >= 40 ? YELLOW : RED;

type PeriodType = "thisWeek" | "thisMonth" | "lastMonth" | "last3Months" | "thisYear" | "custom";

// ─── 날짜 유틸 ─────────────────────────────────────────────────────────────────
function toStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

function toDisp(s: string): string {
    return s.replace(/-/g, ".");
}

function daysBetween(start: string, end: string): number {
    const s = new Date(start + "T00:00:00").getTime();
    const e = new Date(end   + "T00:00:00").getTime();
    return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
}

function calcPeriod(p: PeriodType): { start: string; end: string } {
    const today = new Date();
    const todayStr = toStr(today);
    if (p === "thisWeek") {
        const day = today.getDay();
        const mon = new Date(today);
        mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
        return { start: toStr(mon), end: todayStr };
    }
    if (p === "thisMonth") {
        return { start: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`, end: todayStr };
    }
    if (p === "lastMonth") {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last  = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: toStr(first), end: toStr(last) };
    }
    if (p === "last3Months") {
        const first = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        return { start: toStr(first), end: todayStr };
    }
    if (p === "thisYear") {
        return { start: `${today.getFullYear()}-01-01`, end: todayStr };
    }
    return { start: todayStr, end: todayStr };
}

// ─── 컴포넌트 ──────────────────────────────────────────────────────────────────
const ConsultationStats: React.FC<any> = (props: any) => {

    const initPeriod = calcPeriod("last3Months");
    const [period,      setPeriod]      = useState<PeriodType>("last3Months");
    const [startDate,   setStartDate]   = useState<string>(initPeriod.start);
    const [endDate,     setEndDate]     = useState<string>(initPeriod.end);
    const [customStart, setCustomStart] = useState<string>(initPeriod.start);
    const [customEnd,   setCustomEnd]   = useState<string>(initPeriod.end);
    const [showCustom,  setShowCustom]  = useState<boolean>(false);

    const [summary,     setSummary]     = useState<any>(null);
    const [byCounselor, setByCounselor] = useState<any[]>([]);
    const [loading,     setLoading]     = useState<boolean>(false);

    const [sortCounselorBy, setSortCounselorBy] = useState<"rate" | "count">("rate");
    const [hideSmallSample, setHideSmallSample] = useState<boolean>(true);

    // academy 기준 지점 한글 매핑
    const academyLocations = useMemo(() => {
        if (!props.user?.academy) return [];
        return totalLocationData.filter((loc: any) => loc.academy.includes(props.user.academy));
    }, [props.user?.academy]);

    const koreanLoc = (eng: string): string => {
        return academyLocations.find((l: any) => l.english === eng)?.korean || eng;
    };

    useEffect(() => {
        if (!startDate || !endDate) return;
        fetchAll(startDate, endDate);
    }, [startDate, endDate]); // eslint-disable-line

    const fetchAll = async (start: string, end: string) => {
        setLoading(true);
        try {
            const [s, c]: any[] = await Promise.all([
                postFetch("/charts/consultation/summary",     { startDate: start, endDate: end }),
                postFetch("/charts/consultation/byCounselor", { startDate: start, endDate: end }),
            ]);
            if (s?.message === "success") setSummary(s);
            if (c?.message === "success") setByCounselor(c.byCounselor || []);
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    };

    const applyPeriod = (p: PeriodType) => {
        setPeriod(p);
        if (p === "custom") { setShowCustom(true); return; }
        setShowCustom(false);
        const { start, end } = calcPeriod(p);
        setStartDate(start);
        setEndDate(end);
    };

    const applyCustom = () => {
        if (!customStart || !customEnd) return;
        setStartDate(customStart);
        setEndDate(customEnd);
        setShowCustom(false);
    };

    const totals    = summary?.totals;
    const momChange = summary?.momChange;
    const top       = summary?.top;
    const bottom    = summary?.bottom;
    const byLocation = summary?.byLocation || [];

    // 지점별 막대용 데이터 (등록률 내림차순)
    const locationBarData = useMemo(() => {
        return [...byLocation]
            .map((r: any) => ({ ...r, name: koreanLoc(r.location) }))
            .sort((a: any, b: any) => b.registerRate - a.registerRate);
    }, [byLocation, academyLocations]);

    // 지점별 상담 진행률 (실제 상담 / 상담 신청) — 진행률 내림차순
    const locationProgressData = useMemo(() => {
        return [...byLocation]
            .map((r: any) => ({
                ...r,
                name: koreanLoc(r.location),
                progressRate: r.applications > 0 ? +(r.count / r.applications * 100).toFixed(1) : 0,
            }))
            .sort((a: any, b: any) => b.progressRate - a.progressRate);
    }, [byLocation, academyLocations]);

    // 담당자별 표시 데이터
    const counselorRows = useMemo(() => {
        const rows = byCounselor.map((r: any) => ({
            ...r,
            locationKorean: koreanLoc(r.location),
            isSmall: r.count < 3,
        }));

        const sorted = [...rows].sort((a: any, b: any) => {
            // 표본 부족은 항상 뒤로
            if (a.isSmall !== b.isSmall) return a.isSmall ? 1 : -1;
            if (sortCounselorBy === "rate") return b.registerRate - a.registerRate;
            return b.count - a.count;
        });

        return hideSmallSample ? sorted.filter((r: any) => !r.isSmall) : sorted;
    }, [byCounselor, sortCounselorBy, hideSmallSample, academyLocations]);

    // ─── KPI 카드 ────────────────────────────────────────────────────────────────
    const KpiCard = ({ label, value, sub, subPositive }: {
        label: string; value: string | number; sub?: string; subPositive?: boolean;
    }) => (
        <div style={{
            background: CARD_BG, borderRadius: 12, padding: "18px 20px", flex: 1, minWidth: 0,
            border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
            <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: TEXT_MAIN, lineHeight: 1.2 }}>
                {value}
            </div>
            {sub !== undefined && (
                <div style={{
                    marginTop: 6, fontSize: 13, fontWeight: 600,
                    color: subPositive === undefined ? TEXT_SUB : subPositive ? "#059669" : "#DC2626",
                }}>
                    {sub}
                </div>
            )}
        </div>
    );

    const periodBtnStyle = (p: PeriodType): React.CSSProperties => ({
        padding: "6px 14px", borderRadius: 20,
        border: `1px solid ${period === p ? "#4F46E5" : BORDER}`,
        background: period === p ? "#4F46E5" : CARD_BG,
        color: period === p ? "#fff" : TEXT_SUB,
        cursor: "pointer", fontSize: 13, fontWeight: period === p ? 600 : 400,
    });

    // 인사이트 박스 본문 헬퍼
    const topMultiple = (top && totals?.registerRate > 0)
        ? (top.registerRate / totals.registerRate).toFixed(1)
        : null;
    const bottomDivisor = (bottom && bottom.registerRate > 0 && totals?.registerRate > 0)
        ? Math.round(totals.registerRate / bottom.registerRate)
        : null;

    return (
        <div style={{ padding: "24px 28px", background: PAGE_BG, color: TEXT_MAIN, minHeight: "100%" }}>

            {/* 헤더 */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>
                    통계 · 영업 효율
                </div>
                <div style={{ fontSize: 12, color: TEXT_SUB }}>
                    방문 상담 → 최종 등록 전환을 지점별·담당자별로 추적
                </div>
            </div>

            {/* 잠정치 안내 */}
            <div style={{ marginBottom: 16, fontSize: 13, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 14px", display: "inline-block" }}>
                ⚠️ 최근 데이터는 잠정치일 수 있습니다.
            </div>

            {/* ───────────── 영역 1: 기간 필터 ───────────── */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                <span style={{ fontSize: 13, color: TEXT_SUB, marginRight: 4 }}>기간</span>
                {(["thisWeek", "thisMonth", "lastMonth", "last3Months", "thisYear", "custom"] as PeriodType[]).map((p) => (
                    <button key={p} style={periodBtnStyle(p)} onClick={() => applyPeriod(p)}>
                        {p === "thisWeek" ? "이번 주" : p === "thisMonth" ? "이번 달" : p === "lastMonth" ? "지난 달" : p === "last3Months" ? "최근 3개월" : p === "thisYear" ? "올해" : "사용자 지정"}
                    </button>
                ))}
                <span style={{ marginLeft: "auto", fontSize: 13, color: TEXT_SUB }}>
                    {toDisp(startDate)} — {toDisp(endDate)} ({daysBetween(startDate, endDate)}일)
                </span>
            </div>

            {showCustom && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                    <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                        style={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN, borderRadius: 8, padding: "6px 10px", fontSize: 13 }} />
                    <span style={{ color: TEXT_SUB }}>—</span>
                    <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                        style={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN, borderRadius: 8, padding: "6px 10px", fontSize: 13 }} />
                    <button onClick={applyCustom}
                        style={{ padding: "6px 16px", background: "#4F46E5", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13 }}>
                        적용
                    </button>
                </div>
            )}

            {/* ───────────── 영역 2: KPI 카드 ───────────── */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <KpiCard
                    label="총 상담"
                    value={totals?.count ?? 0}
                    sub={momChange ? `${momChange.countPct >= 0 ? "+" : ""}${momChange.countPct}% MoM` : undefined}
                    subPositive={(momChange?.countPct ?? 0) >= 0}
                />
                <KpiCard
                    label="최종 등록"
                    value={totals?.registered ?? 0}
                    sub={momChange ? `${momChange.registeredPct >= 0 ? "+" : ""}${momChange.registeredPct}% MoM` : undefined}
                    subPositive={(momChange?.registeredPct ?? 0) >= 0}
                />
                <KpiCard
                    label="전사 등록률"
                    value={`${totals?.registerRate ?? 0}%`}
                    sub={momChange ? `${momChange.registerRateDiff >= 0 ? "+" : ""}${momChange.registerRateDiff}p MoM` : undefined}
                    subPositive={(momChange?.registerRateDiff ?? 0) >= 0}
                />
                <KpiCard
                    label="활성 담당자"
                    value={summary?.activeCounselors ?? 0}
                    sub={summary ? `${(summary.activeCounselors - summary.prevActiveCounselors) >= 0 ? "+" : ""}${summary.activeCounselors - summary.prevActiveCounselors}` : undefined}
                    subPositive={summary ? (summary.activeCounselors - summary.prevActiveCounselors) >= 0 : undefined}
                />
            </div>

            {/* ───────────── 영역 3: 자동 인사이트 박스 ───────────── */}
            <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                {/* TOP 박스 */}
                <div style={{
                    flex: 1, background: top ? "#ECFDF5" : "#F3F4F6",
                    borderLeft: `4px solid ${top ? GREEN : BORDER}`,
                    borderRadius: 8, padding: "14px 20px",
                }}>
                    <div style={{ fontSize: 12, color: top ? "#065F46" : TEXT_SUB, fontWeight: 600, marginBottom: 6 }}>
                        💡 영업력 TOP
                    </div>
                    {top ? (
                        <div style={{ fontSize: 13, color: TEXT_MAIN, lineHeight: 1.5 }}>
                            <b>{koreanLoc(top.location)} 등록률 {top.registerRate}%</b> ({top.count}건 상담 → {top.registered}건 등록).
                            {topMultiple && Number(topMultiple) >= 1.1 ? ` 전사 평균의 약 ${topMultiple}배.` : ""}
                        </div>
                    ) : (
                        <div style={{ fontSize: 13, color: TEXT_SUB }}>표본 부족 (지점별 상담 3건 이상 필요)</div>
                    )}
                </div>

                {/* 주의 박스 */}
                <div style={{
                    flex: 1, background: bottom ? "#FEF2F2" : "#F3F4F6",
                    borderLeft: `4px solid ${bottom ? RED : BORDER}`,
                    borderRadius: 8, padding: "14px 20px",
                }}>
                    <div style={{ fontSize: 12, color: bottom ? "#991B1B" : TEXT_SUB, fontWeight: 600, marginBottom: 6 }}>
                        ⚠️ 주의 필요
                    </div>
                    {bottom ? (
                        <div style={{ fontSize: 13, color: TEXT_MAIN, lineHeight: 1.5 }}>
                            <b>{koreanLoc(bottom.location)} 등록률 {bottom.registerRate}%</b> ({bottom.count}건 상담 → {bottom.registered}건 등록).
                            {bottomDivisor && bottomDivisor >= 2 ? ` 전사 평균의 1/${bottomDivisor} 수준.` : ""}
                        </div>
                    ) : (
                        <div style={{ fontSize: 13, color: TEXT_SUB }}>표본 부족</div>
                    )}
                </div>
            </div>

            {/* ───────────── 영역 4: 지점별 등록률 (수평 막대) ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>지점별 결제율</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 16 }}>상담수 대비 최종 등록 비율 (높은 순)</div>

                {locationBarData.length === 0 ? (
                    <div style={{ padding: "40px 0", textAlign: "center", color: TEXT_SUB, fontSize: 13 }}>데이터 없음</div>
                ) : (
                    <ResponsiveContainer width="100%" height={Math.max(300, locationBarData.length * 36)}>
                        <BarChart
                            data={locationBarData}
                            layout="vertical"
                            margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} horizontal={false} />
                            <XAxis
                                type="number"
                                domain={[0, 100]}
                                ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                                tickFormatter={(v: number) => `${v}%`}
                                tick={{ fill: TEXT_SUB, fontSize: 11 }}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={70}
                                tick={{ fill: TEXT_MAIN, fontSize: 12 }}
                            />
                            <Tooltip
                                contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }}
                                formatter={(value: any, _name: any, props: any) => {
                                    const r = props?.payload || {};
                                    return [`${value}% (${r.count}건 → ${r.registered}건)`, r.name];
                                }}
                            />
                            <Bar dataKey="registerRate" radius={[0, 4, 4, 0]}>
                                {locationBarData.map((r: any, idx: number) => (
                                    <Cell key={idx} fill={colorByRate(r.registerRate)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* ───────────── 영역 5: 지점별 상세 표 ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px 16px", marginBottom: 8, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>지점별 상세</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 12 }}>기간 내 누적 데이터</div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "#F9FAFB" }}>
                            <th style={{ padding: "10px 14px", textAlign: "left",  color: TEXT_SUB, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>지점</th>
                            <th style={{ padding: "10px 14px", textAlign: "right", color: TEXT_SUB, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>상담 신청</th>
                            <th style={{ padding: "10px 14px", textAlign: "right", color: TEXT_SUB, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>상담</th>
                            <th style={{ padding: "10px 14px", textAlign: "right", color: TEXT_SUB, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>최종 등록</th>
                            <th style={{ padding: "10px 14px", textAlign: "right", color: TEXT_SUB, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${BORDER}` }}>결제율</th>
                        </tr>
                    </thead>
                    <tbody>
                        {locationBarData.map((row: any, idx: number) => (
                            <tr key={row.location} style={{ background: idx % 2 === 0 ? CARD_BG : "#F9FAFB", borderBottom: `1px solid ${BORDER}` }}>
                                <td style={{ padding: "10px 14px", color: TEXT_MAIN, fontWeight: 600 }}>{row.name}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.applications}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.count}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: TEXT_MAIN }}>{row.registered}</td>
                                <td style={{ padding: "10px 14px", textAlign: "right", color: colorByRate(row.registerRate), fontWeight: 700 }}>
                                    {row.registerRate}%
                                </td>
                            </tr>
                        ))}
                        {totals && (
                            <tr style={{ background: "#EEF2FF", borderTop: `2px solid ${BORDER}` }}>
                                <td style={{ padding: "11px 14px", color: TEXT_MAIN, fontWeight: 700 }}>전사 합계</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{totals.applications}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{totals.count}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{totals.registered}</td>
                                <td style={{ padding: "11px 14px", textAlign: "right", color: TEXT_MAIN, fontWeight: 700 }}>{totals.registerRate}%</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* 범례 */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24, paddingLeft: 8, fontSize: 12, color: TEXT_SUB }}>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: GREEN, marginRight: 6 }} />60% 이상</span>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: YELLOW, marginRight: 6 }} />40~60%</span>
                <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: RED, marginRight: 6 }} />40% 미만</span>
            </div>

            {/* ───────────── 영역 6: 담당자별 결제율 (수평 막대) ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>담당자별 결제율</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 16 }}>상담 3건 이상 담당자만 표시</div>

                {/* 컨트롤 */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: TEXT_MAIN, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={hideSmallSample}
                            onChange={(e) => setHideSmallSample(e.target.checked)}
                            style={{ cursor: "pointer" }}
                        />
                        표본 부족(3건 미만) 숨기기
                    </label>
                    <button
                        onClick={() => setSortCounselorBy(sortCounselorBy === "rate" ? "count" : "rate")}
                        style={{
                            padding: "4px 10px", borderRadius: 6,
                            border: `1px solid ${BORDER}`, background: CARD_BG,
                            color: TEXT_SUB, cursor: "pointer", fontSize: 12,
                        }}
                    >
                        정렬: {sortCounselorBy === "rate" ? "결제율" : "상담수"} ▼
                    </button>
                </div>

                {counselorRows.length === 0 ? (
                    <div style={{ padding: "40px 0", textAlign: "center", color: TEXT_SUB, fontSize: 13 }}>표시할 담당자가 없습니다</div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {counselorRows.map((r: any) => {
                            const barColor = r.isSmall ? "#9CA3AF" : colorByRate(r.registerRate);
                            const labelColor = r.isSmall ? TEXT_SUB : colorByRate(r.registerRate);
                            return (
                                <div key={`${r.assignedUserId}__${r.location}`} style={{
                                    display: "grid",
                                    gridTemplateColumns: "140px 70px 1fr 60px",
                                    alignItems: "center",
                                    gap: 12,
                                    opacity: r.isSmall ? 0.4 : 1,
                                }}>
                                    {/* 이름 + 지점·건수 */}
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_MAIN }}>{r.assignedUserName}</div>
                                        <div style={{ fontSize: 11, color: TEXT_SUB, marginTop: 2 }}>{r.locationKorean} · {r.count}건</div>
                                    </div>

                                    {/* 등록수 */}
                                    <div style={{ fontSize: 13, color: TEXT_MAIN }}>{r.registered} 등록</div>

                                    {/* 막대 */}
                                    <div style={{ position: "relative", height: 14, background: "#F3F4F6", borderRadius: 7, overflow: "hidden" }}>
                                        <div style={{
                                            position: "absolute", top: 0, left: 0, bottom: 0,
                                            width: `${Math.min(r.registerRate, 100)}%`,
                                            background: barColor,
                                            borderRadius: 7,
                                            transition: "width 0.3s",
                                        }} />
                                    </div>

                                    {/* 퍼센트 */}
                                    <div style={{ fontSize: 13, fontWeight: 700, color: labelColor, textAlign: "right" }}>
                                        {r.registerRate}%
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ───────────── 영역 7: 지점별 상담 진행률 (수평 막대) ───────────── */}
            <div style={{ background: CARD_BG, borderRadius: 12, padding: "20px 24px", marginBottom: 24, border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4 }}>지점별 상담 진행률</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 16 }}>상담 신청 대비 실제 상담 진행 비율 (높은 순)</div>

                {locationProgressData.length === 0 ? (
                    <div style={{ padding: "40px 0", textAlign: "center", color: TEXT_SUB, fontSize: 13 }}>데이터 없음</div>
                ) : (
                    <ResponsiveContainer width="100%" height={Math.max(300, locationProgressData.length * 36)}>
                        <BarChart
                            data={locationProgressData}
                            layout="vertical"
                            margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} horizontal={false} />
                            <XAxis
                                type="number"
                                domain={[0, 100]}
                                ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                                tickFormatter={(v: number) => `${v}%`}
                                tick={{ fill: TEXT_SUB, fontSize: 11 }}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={70}
                                tick={{ fill: TEXT_MAIN, fontSize: 12 }}
                            />
                            <Tooltip
                                contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }}
                                formatter={(value: any, _name: any, props: any) => {
                                    const r = props?.payload || {};
                                    return [`${value}% (${r.applications}건 신청 → ${r.count}건 진행)`, r.name];
                                }}
                            />
                            <Bar dataKey="progressRate" radius={[0, 4, 4, 0]}>
                                {locationProgressData.map((r: any, idx: number) => (
                                    <Cell key={idx} fill={colorByRate(r.progressRate)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            {loading && (
                <div style={{ position: "fixed", top: 20, right: 24, background: "#4F46E5", color: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 13, zIndex: 9999 }}>
                    로딩 중…
                </div>
            )}
        </div>
    );
};

export default ConsultationStats;
